import axios, { type AxiosHeaders, type AxiosResponse } from 'axios';
import { getAuthHeaders } from '../Auth';
import type { SurfaceSpecification } from '../types/SurfaceSpecification';
import { resolveProductSku } from '../tools/resolveProductSku';
export type { SurfaceSpecification };

// API types
// Partial result from
// https://scenes.documents.cimpress.io/swagger/index.html
interface HalAssetLinkHalQueryResult {
    _embedded: HalAssetLinkHalEmbeddedCollection;
}

interface HalAssetLinkHalEmbeddedCollection {
    item: HalAssetLink[];
}

interface HalAssetLink {
    _links: HalAssetLinkLinks;
}

interface HalAssetLinkLinks {
    self: HalLink;
    content: HalLink;
    variableConfigurationContent: HalLink;
}

interface HalLink {
    href: string | null;
}

interface InterrogationResponse {
    Width: number;
    Height: number;
    Slots: Slot[];
}

interface Slot {
    Id: string;
    Page: number;
}

interface ProductResponse {
    name: string;
    version?: number;
}

export interface DetectedLogoInfo {
    detectedLogoUrl: string;
    confidence?: number;
}

export interface ComposeDebugInfo {
    detectedLogos?: DetectedLogoInfo[];
    confidenceOfLogoUsedForReplacement?: number;
    replacedImagePreviewUrl?: string;
}

export interface TextClassifyResult {
    text: string;
    label: string;
    confidence?: number;
}

export interface AgsDebugInfoHeader {
    compose?: ComposeDebugInfo;
}

interface LookupPreviewsResponse {
    views: {
        name: string;
        scene: {
            url: string;
        };
    }[];
}

export const getProductSceneUrl = async (
    productId: string,
    productVersion: number,
    productOptions: Record<string, string>,
): Promise<string | null> => {
    try {
        const tenant = { id: 'vistaprint-prod', type: 'merchants' };
        const purpose = 'design.studio.review';
        const previewTypes = ['scene'];
        const scenesUrl = `https://lookup.previews.cimpress.io/api/v2/views?referenceId=${productId}&referenceVersion=${productVersion}&attributes=${encodeURIComponent(JSON.stringify(productOptions))}&tenant=${encodeURIComponent(JSON.stringify(tenant))}&purpose=${encodeURIComponent(purpose)}&previewTypes=${encodeURIComponent(JSON.stringify(previewTypes))}`;
        const headers = await getAuthHeaders();
        const result = await axios.get<LookupPreviewsResponse>(scenesUrl, {
            headers,
        });
        const view = result?.data?.views.find(({ name }) => name.toLowerCase() === 'front') ?? result?.data?.views?.[0];
        const scene = view?.scene?.url ?? null;
        return scene;
    } catch (error) {
        console.error(`An error occurred while fetching photo scene URL: ${error}`);
        return null;
    }
};

export const getPhotoSceneUrl = async (surfaceUrl: string, signal?: AbortSignal): Promise<string | null> => {
    try {
        const surfaceUrlObj = new URL(surfaceUrl);
        const mcpSku = surfaceUrlObj.pathname.split('/').find((x) => new RegExp(/[A-Z]{3}-[A-Z0-9]{8}/, 'g').test(x));
        if (!mcpSku) return null;
        const productOptions = surfaceUrlObj.searchParams.get('variables');
        const productOptionsObj = productOptions ? JSON.parse(productOptions) : undefined;
        const requestBody = {
            referenceId: mcpSku,
            Tenant: { type: 'merchants', id: 'vistaprint-prod' },
            attributes: productOptionsObj,
            assetTypes: ['scene'],
        } as const;
        const linksQueryUrl = 'https://scenes.documents.cimpress.io/v3/assets/-/links:query';
        const headers = await getAuthHeaders();
        const result = await axios.post<HalAssetLinkHalQueryResult>(linksQueryUrl, requestBody, {
            headers,
            signal,
        });
        const scenes = result?.data?._embedded?.item;
        const sceneForFirstPage = !!scenes && scenes.length > 0 ? await getSceneForFirstPage(scenes, signal) : null;
        return sceneForFirstPage;
    } catch (error) {
        if (signal?.aborted) {
            throw error;
        }

        console.error(`An error occurred while fetching photo scene URL: ${error}`);
        return null;
    }
};

async function getSceneForFirstPage(sceneUrls: HalAssetLink[], signal?: AbortSignal): Promise<string | null> {
    const allScenes = await Promise.all(
        sceneUrls.map(async (sceneUrl) => {
            try {
                if (!sceneUrl?._links?.content?.href) return null;
                const interrogationUrl = `https://assets.documents.cimpress.io/v1/interrogation?sceneUri=${sceneUrl._links.content.href}`;
                const headers = await getAuthHeaders();
                const response = await axios.get<InterrogationResponse>(interrogationUrl, {
                    headers,
                    signal,
                });
                return { url: sceneUrl._links.content.href, data: response.data };
            } catch (exception) {
                if (signal?.aborted) {
                    throw exception;
                }

                return null;
            }
        }),
    );
    const firstPageSceneData = allScenes.find((sceneData) => sceneData && sceneData.data?.Slots[0]?.Page === 1);
    return firstPageSceneData?.url ?? null;
}

// API Documentation
// https://developer.cimpress.io/software-catalog/component/api/bc8d6c62-a6a0-50a9-ba72-fc883798972d/openapi?env=production
export async function getProductNameBasedOnSku(skuOrUrl: string, signal?: AbortSignal): Promise<string | null> {
    let targetSku = skuOrUrl;

    if (skuOrUrl.includes('://')) {
        const convertedCimSku = await resolveProductSku(skuOrUrl, signal);
        if (convertedCimSku !== skuOrUrl) {
            targetSku = convertedCimSku;
        } else {
            // It might be a calculated surface set URL (e.g. /v3/calculatedSurfaceSets?surfaceSetLink=...)
            const surfaceData = await getSurfaceMcpSku(skuOrUrl, signal);
            if (surfaceData?.mcpSku) {
                targetSku = await resolveProductSku(surfaceData.mcpSku, signal);
            } else {
                return null;
            }
        }
    } else if (skuOrUrl.startsWith('PRD-')) {
        targetSku = await resolveProductSku(skuOrUrl, signal);
    }

    const productUrl = `https://api.products.cimpress.io/v1/products/${targetSku}:current`;
    try {
        const headers = await getAuthHeaders();
        const response = await axios.get<ProductResponse>(productUrl, {
            headers,
            signal,
        });
        return response.data?.name ?? null;
    } catch (error) {
        if (signal?.aborted) {
            throw error;
        }

        return null;
    }
}

/**
 * Fetches the calculated surface set URL and extracts the mcpSku from the response.
 */
export async function getSurfaceMcpSku(surfaceUrl: string, signal?: AbortSignal): Promise<SurfaceSpecification | null> {
    try {
        const headers = await getAuthHeaders();
        const response = await axios.get<SurfaceSpecification>(surfaceUrl, {
            headers,
            signal,
        });
        return response.data;
    } catch (error) {
        if (signal?.aborted) {
            throw error;
        }
        console.error('Error fetching mcpSku from surface URL:', error);
        return null;
    }
}

/**
 * Fetches the current product version for a given mcpSku or surface URL.
 */
export async function getProductCurrentVersion(skuOrUrl: string, signal?: AbortSignal): Promise<number | null> {
    let targetSku = skuOrUrl;

    if (skuOrUrl.includes('://') || skuOrUrl.startsWith('PRD-')) {
        targetSku = await resolveProductSku(skuOrUrl, signal);
    }

    const productUrl = `https://api.products.cimpress.io/v1/products/${targetSku}:current`;
    try {
        const headers = await getAuthHeaders();
        const response = await axios.get<ProductResponse>(productUrl, {
            headers,
            signal,
        });
        return response.data?.version ?? null;
    } catch (error) {
        if (signal?.aborted) {
            throw error;
        }
        console.error('Error fetching product version for SKU:', targetSku, error);
        return null;
    }
}

export function getAgsDebugInfoHeaderFromResponse(response: AxiosResponse) {
    const headers = response.headers as AxiosHeaders;
    const agsDebugInfoString = headers?.get('x-ags-debug-info') as string;
    if (agsDebugInfoString) {
        const agsDebugInfo = JSON.parse(agsDebugInfoString);
        return agsDebugInfo as AgsDebugInfoHeader;
    }
    return undefined;
}

export async function getAgsDebugInfoHeader(url: string, signal?: AbortSignal) {
    const response = await axios.get(url, { signal });
    return getAgsDebugInfoHeaderFromResponse(response);
}
